import { gql } from "@apollo/client";
import {
  DATA_COLUMN_FIELDS_FRAGMENT,
  KPI_FIELDS_FRAGMENT,
} from "@pages/mmm/explore-budgets-v2/explore-budgets-queries";

const MEDIA_PLAN_VERIFY_STEP_FIELDS_FRAGMENT = gql`
  fragment MediaPlanVerifyStepFields on StepVerification {
    verified
    verified_data
    verified_by_user_id
    verified_at
    pending_data
  }
`;

const MEDIA_PLAN_FIELDS_FRAGMENT = gql`
  ${KPI_FIELDS_FRAGMENT}
  ${DATA_COLUMN_FIELDS_FRAGMENT}
  ${MEDIA_PLAN_VERIFY_STEP_FIELDS_FRAGMENT}
  fragment MediaPlanFields on mmmMediaPlan {
    id
    model {
      id
      is_blessed
      model_path
      create_northbeam_mmm_run_result
      run_result {
        cycle_dataset_path
      }
    }
    model_config {
      config_id
      client_seed_dataset
      config_name
      is_active
    }
    date_range {
      id
      start_date
      end_date
    }
    state
    is_current
    is_errored
    breakdowns
    is_manually_overridden
    hidden
    name
    kpis {
      ...KpiFields
    }
    spend_model_data_columns {
      ...DataColumnFields
    }
    calendar_of_events {
      id
      description
      annotatedDate
      startDate
      endDate
      campaignType
    }
    media_plan_draft_id
    additional_data_inputs
    step_tracker {
      verifiedChannels {
        ...MediaPlanVerifyStepFields
      }
      verifiedPerformanceGoals {
        ...MediaPlanVerifyStepFields
      }
      verifiedCalendarOfEvents {
        ...MediaPlanVerifyStepFields
      }
      verifiedAdditionalData {
        ...MediaPlanVerifyStepFields
      }
    }
  }
`;

export const BASIC_MEDIA_PLAN_FIELDS_FRAGMENT = gql`
  ${KPI_FIELDS_FRAGMENT}
  ${DATA_COLUMN_FIELDS_FRAGMENT}
  fragment BasicMediaPlanFields on basicMediaPlan {
    id
    date_range {
      start_date
      end_date
    }
    name
    hidden
    model_config {
      config_id
      client_seed_dataset
      config_name
      is_active
    }
    breakdowns
    kpis {
      ...KpiFields
    }
    spend_model_data_columns {
      ...DataColumnFields
    }
    state
    is_errored
    media_plan_draft_id
    blessed_model_id
    connected_model_id
  }
`;

export const GET_BASIC_MEDIA_PLANS = gql`
  ${BASIC_MEDIA_PLAN_FIELDS_FRAGMENT}
  query GetBasicMediaPlans {
    me {
      mmm {
        basicMediaPlansData {
          plans {
            ...BasicMediaPlanFields
          }
          modelConfigurations {
            config_id
            config_name
          }
        }
      }
    }
  }
`;

export const CREATE_BASIC_MEDIA_PLAN = gql`
  ${BASIC_MEDIA_PLAN_FIELDS_FRAGMENT}
  mutation CreateBasicMediaPlan(
    $start_date: String!
    $end_date: String!
    $model_configuration_id: String!
    $name: String!
  ) {
    createBasicMediaPlanDraft(
      start_date: $start_date
      end_date: $end_date
      model_configuration_id: $model_configuration_id
      name: $name
    ) {
      ...BasicMediaPlanFields
    }
  }
`;

export const FINALIZE_BASIC_MEDIA_PLAN = gql`
  mutation FinalizeBasicMediaPlan($id: String!, $modelId: String!) {
    finalizeBasicMediaPlan(id: $id, modelId: $modelId)
  }
`;

export const UPDATE_BASIC_MEDIA_PLAN = gql`
  ${BASIC_MEDIA_PLAN_FIELDS_FRAGMENT}
  mutation UpdateBasicMediaPlan(
    $id: String!
    $start_date: String!
    $end_date: String!
    $model_configuration_id: String!
    $name: String!
  ) {
    updateBasicMediaPlanDraft(
      id: $id
      start_date: $start_date
      end_date: $end_date
      model_configuration_id: $model_configuration_id
      name: $name
    ) {
      ...BasicMediaPlanFields
    }
  }
`;

export const FETCH_MEDIA_PLAN_DATA = gql`
  ${MEDIA_PLAN_FIELDS_FRAGMENT}
  query FetchMediaPlanData {
    me {
      mmm {
        mediaPlanData {
          modelConfigurations {
            config_id
            client_seed_dataset
            config_name
            is_active
          }
          mediaPlanList {
            ...MediaPlanFields
          }
        }
      }
    }
  }
`;

export const CREATE_MEDIA_PLAN_DRAFT = gql`
  ${MEDIA_PLAN_FIELDS_FRAGMENT}
  mutation CreateMediaPlanDraft(
    $start_date: String!
    $end_date: String!
    $model_configuration_id: String!
    $name: String!
    $client_seed_dataset_id: String!
  ) {
    createMediaPlanDraft(
      start_date: $start_date
      end_date: $end_date
      model_configuration_id: $model_configuration_id
      name: $name
      client_seed_dataset_id: $client_seed_dataset_id
    ) {
      ...MediaPlanFields
    }
  }
`;

export const UPDATE_MEDIA_PLAN_DRAFT = gql`
  ${MEDIA_PLAN_FIELDS_FRAGMENT}
  mutation UpdateMediaPlanDraft(
    $id: String!
    $start_date: String!
    $end_date: String!
    $model_configuration_id: String!
    $name: String!
    $client_seed_dataset_id: String!
  ) {
    updateMediaPlanDraft(
      id: $id
      start_date: $start_date
      end_date: $end_date
      model_configuration_id: $model_configuration_id
      name: $name
      client_seed_dataset_id: $client_seed_dataset_id
    ) {
      ...MediaPlanFields
    }
  }
`;

export const UPDATE_MEDIA_PLAN_DRAFT_STEP = gql`
  ${MEDIA_PLAN_FIELDS_FRAGMENT}
  mutation UpdateMediaPlanDraftStep(
    $id: Int!
    $draft_step: String!
    $verified: Boolean!
    $verified_data: [JSONObject]!
    $pending_data: [String]!
  ) {
    updateMediaPlanDraftStep(
      id: $id
      draft_step: $draft_step
      verified: $verified
      verified_data: $verified_data
      pending_data: $pending_data
    ) {
      ...MediaPlanFields
    }
  }
`;

export const TRIGGER_MEDIA_PLAN_MODEL_TRAINING = gql`
  mutation TriggerMediaPlanModelTraining(
    $cycle_id: String!
    $model_config_id: String!
  ) {
    triggerOptimizerModelTraining(
      cycle_id: $cycle_id
      model_config_id: $model_config_id
    )
  }
`;
